import { ExpandMore } from '@mui/icons-material';
import { Grid, Box, Divider } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { IWorkspaceUserFilter } from 'wisestep-api';
import {
  WsButton,
  WsCheckboxList,
  WsColors,
  WsElementWithPopOver,
  SearchBarNew,
  WsRadioGroup,
  WsBadge,
  WsBadgetype,
  WsBadgeSize,
  WsColorsNew,
  WsPageHeader,
} from 'wisestep-base';

import { extractEmail } from '../../../utils/StringUtils';
import {
  workspaceUsersPayload,
  getUsersRolesCheckBoxOptions,
  statusOptions,
  pushUsersFilters,
  workspaceAccountManagerPayload,
} from '../helper/defaultValues';

interface IUsersFilters {
  query: IWorkspaceUserFilter;
  setQuery: Dispatch<SetStateAction<IWorkspaceUserFilter>>;
  forAccountManagerListing?: boolean;
}

const UsersFilterDetailsNew = ({ query, setQuery, forAccountManagerListing }: IUsersFilters) => {
  const methods = useForm<IWorkspaceUserFilter>({
    defaultValues: { ...query },
  });

  const { reset, getValues, handleSubmit } = methods;

  const clearAllFields = () => {
    if (forAccountManagerListing) {
      setQuery({ ...workspaceAccountManagerPayload, roles: ['Account Manager'] });
      reset({ ...workspaceAccountManagerPayload, roles: ['Account Manager'] });
    } else {
      setQuery({ ...workspaceUsersPayload });
      reset({ ...workspaceUsersPayload });
    }
  };
  const filtersArray = useMemo(() => pushUsersFilters(query, true), [query]);

  const onDeleteFilterTag = (tag: string) => {
    if (tag?.includes('Account Manager') && forAccountManagerListing) return;
    const updatedQuery = { ...query };
    const idOfChip = filtersArray.find((existChip) => existChip.tag === tag)?.id ?? '';
    updatedQuery[idOfChip] = null;
    if (isEmpty(updatedQuery.statuses)) updatedQuery.statuses = ['all'];
    setQuery({ ...updatedQuery });
  };

  const getUsersByStatus = (selectedvalue) => {
    const statuses = selectedvalue;
    setQuery({
      ...query,
      statuses: isEmpty(statuses) ? workspaceUsersPayload.statuses : statuses,
      page: 0,
    });
  };

  const getUsersByRoles = () => {
    const roles = getValues('roles');
    setQuery({ ...query, roles: roles, page: 0 });
  };

  useEffect(() => reset({ ...query }), [reset, query]);

  const onSearch = (data: IWorkspaceUserFilter) =>
    setQuery({
      ...query,
      userIds: data.userIds?.map((email) => extractEmail(email)),
      page: 0,
      query: data.query,
    });

  return (
    <Grid container item xs={12} spacing={1} bgcolor={WsColors.White}>
      <Grid item display="flex" alignItems="center" xs={8}>
        <WsPageHeader
          title={forAccountManagerListing ? 'Account Managers' : 'All Users'}
          tooltip="View user by role and invite status"
          transparent={true}
        />
      </Grid>
      <FormProvider {...methods}>
        <Grid
          container
          item
          xs={12}
          spacing={2}
          alignItems="center"
          component="form"
          onSubmit={handleSubmit(onSearch)}
        >
          <Grid item xs={5} ml={2}>
            <SearchBarNew size="small" type="submit" name="query" onClear={clearAllFields} />
          </Grid>
          <Grid item>
            <WsElementWithPopOver
              element={
                <WsButton variant="text" size="medium" endIcon={<ExpandMore />}>
                  User Status
                </WsButton>
              }
            >
              <Box pl={1.5} pb={1}>
                <WsRadioGroup
                  name="statuses"
                  selectArray={statusOptions}
                  row={false}
                  onChange={getUsersByStatus}
                />
              </Box>
            </WsElementWithPopOver>
          </Grid>
          <Grid item>
            {!forAccountManagerListing && (
              <WsElementWithPopOver
                closePopOverOnClick={false}
                element={
                  <WsButton endIcon={<ExpandMore />} variant="text" size="medium">
                    User Role
                  </WsButton>
                }
              >
                <Box pl={1.5} pb={1.5}>
                  <WsCheckboxList name="roles" options={getUsersRolesCheckBoxOptions()} />
                  <WsButton variant="contained" size="small" onClick={getUsersByRoles}>
                    Apply
                  </WsButton>
                </Box>
              </WsElementWithPopOver>
            )}
          </Grid>
        </Grid>
      </FormProvider>
      <Grid container item xs={7} spacing={2} mt={-2} ml={0} alignItems="center">
        {filtersArray.map((label, index) => (
          <Grid item key={index}>
            <WsBadge
              label={`Client status: ${label.tag}`}
              badgeType={WsBadgetype.INACTIVE}
              endIcon={true}
              onClick={() => onDeleteFilterTag(label.tag)}
              badgeSize={WsBadgeSize.LARGE}
            />
          </Grid>
        ))}
        {filtersArray.length > 0 && (
          <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                backgroundColor: WsColorsNew.neutral_400a,
                height: 30,
                mx: 0.5,
              }}
            />
            <WsButton onClick={clearAllFields} variant="text" type="reset">
              Clear Filters
            </WsButton>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default UsersFilterDetailsNew;
